import { HandoffMetric } from 'components/onboarding/handoff-hub/handoff-hub.types';
import {
  ChecklistMetricCriteria,
  ChecklistMetricStatus,
  MultiLocationResult,
} from '../../../handoff-checklist-v2/handoff-checklist-v2.types';
import { deriveOverallStatus } from '../../../handoff-checklist-v2/handoff-checklist-v2.helpers';
import { UnifyLocationRow } from '../unify-locations-table/unify-locations-table.types';
import { UnifyMetricDefinition, UnifyMetricResult } from '../../unify-metrics.types';
import { UNIFY_METRIC_DEFINITIONS } from '../../unify-metric-definitions';

const getMetricKey = (locationId: string, m: HandoffMetric): string => {
  const metricId = (m as { id?: string }).id;
  return metricId
    ? `${locationId}:${metricId}`
    : `${locationId}:${m.section || ''}:${m.subsection || ''}:${m.testCriteria || ''}`;
};

const normalize = (str?: string) => (str || '').trim().toLowerCase();

/**
 * Derives the ChecklistMetricStatus from raw snapshot result and exception string.
 */
const deriveCriteriaStatus = (
  result: string,
  exception?: string
): ChecklistMetricStatus => {
  const trimmed = (exception || '').trim();
  if (result === 'pass') {
    return ChecklistMetricStatus.PASSED;
  }
  if (trimmed !== '' || result === 'exception_noted') {
    return ChecklistMetricStatus.EXCEPTION_NOTED;
  }
  if (result === 'warning') {
    return ChecklistMetricStatus.WARNING;
  }
  return ChecklistMetricStatus.BLOCKED;
};

/**
 * Maps saved snapshot metric rows (grouped by locationId) into UnifyMetricResult objects
 * matching active UNIFY_METRIC_DEFINITIONS.
 *
 * - Preserves user location table ordering.
 * - Matches criteria strictly to prevent cross-metric hijacking.
 * - Only includes definitions that have matching snapshot metrics.
 */
export const mapSnapshotMetricsToUnifyChecklist = (
  metricsByLocation: Record<string, HandoffMetric[]>,
  locations: UnifyLocationRow[] = [],
  metricDefinitions: UnifyMetricDefinition[] = UNIFY_METRIC_DEFINITIONS
): UnifyMetricResult[] => {
  if (!metricsByLocation || Object.keys(metricsByLocation).length === 0) {
    return [];
  }

  // 1. Build an ordered, deduplicated list of locations matching the overview table
  const nameMap = new Map(
    locations.map((loc) => [loc.locationId, loc.locationName || loc.locationId])
  );
  const orderedLocations = Array.from(
    new Set([...locations.map((l) => l.locationId), ...Object.keys(metricsByLocation)])
  )
    .filter((id) => Boolean(id && metricsByLocation[id]))
    .map((id) => ({ locationId: id, locationName: nameMap.get(id) || id }));

  if (orderedLocations.length === 0) {
    return [];
  }

  const matchedMetricKeys = new Set<string>();
  const results: UnifyMetricResult[] = [];

  // 2. Evaluate known metric definitions
  metricDefinitions.forEach((def) => {
    const normalizedDefSection = normalize(def.sectionName);
    const normalizedDefName = normalize(def.name);
    const normalizedDefId = normalize(def.id);

    const locationResults: MultiLocationResult[] = [];

    orderedLocations.forEach(({ locationId, locationName }) => {
      const snapshotMetrics = metricsByLocation[locationId] || [];
      const criterias: ChecklistMetricCriteria[] = [];

      def.criteria.forEach((criteriaDef) => {
        const normalizedCriteriaDesc = normalize(criteriaDef.description);

        // Find an unconsumed snapshot metric matching this criterion
        const matched = snapshotMetrics.find((m) => {
          const key = getMetricKey(locationId, m);
          if (matchedMetricKeys.has(key)) return false;

          const normSection = normalize(m.section);
          const normSubsection = normalize(m.subsection);
          const normTestCriteria = normalize(m.testCriteria);

          // Section must match broad category or metric identifier
          const sectionMatches =
            !normSection ||
            normSection === normalizedDefSection ||
            normSection === normalizedDefId;

          if (!sectionMatches) return false;

          // Subsection matches metric definition name or id
          const subsectionMatches =
            normSubsection === normalizedDefName ||
            normSubsection === normalizedDefId ||
            normSection === normalizedDefId;

          // Direct criteria description match
          const descMatches =
            normTestCriteria === normalizedCriteriaDesc ||
            normSubsection === normalizedCriteriaDesc;

          return descMatches && (subsectionMatches || !normSubsection);
        });

        if (matched) {
          matchedMetricKeys.add(getMetricKey(locationId, matched));

          const status = deriveCriteriaStatus(matched.result, matched.exception);
          const message = status === ChecklistMetricStatus.PASSED ? 'Passed' : undefined;

          criterias.push({
            description: criteriaDef.description,
            status,
            actualValue: matched.actualValue,
            message,
            exception: matched.exception || '',
            exceptionOptions: criteriaDef.exceptionOptions,
            exceptionType: criteriaDef.exceptionType,
          });
        }
      });

      if (criterias.length > 0) {
        locationResults.push({
          locationId,
          locationName,
          criterias,
        });
      }
    });

    if (locationResults.length > 0) {
      const allStatuses = locationResults.flatMap((lr) =>
        lr.criterias.map((c) => c.status)
      );
      const overallStatus = deriveOverallStatus(allStatuses);

      results.push({
        id: def.id,
        name: def.name,
        sectionName: def.sectionName,
        isRequired: def.isRequired !== undefined ? def.isRequired : true,
        allowExceptions: def.allowExceptions !== undefined ? def.allowExceptions : false,
        status: overallStatus,
        isLoading: false,
        locationResults,
      });
    }
  });

  return results;
};
